import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Steps from './steps/index';
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import {
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  getBasicInfo,
  SESSION_STORAGE_BASIC_INFO_KEY,
  SESSION_STORAGE_WHOLE_LIFE_TERM_KEY,
  SESSION_STORAGE_WHOLE_LIFE_HEALTH_KEY,
} from '../../../sessionStorage';
import { saveSessionEvent } from "../../../session";
import CheckSSOLogin from '../Common/CheckSSOLogin';
import './WholeLife.scss';
import { EasyStepsSchedule, EasyStepsConfirmation } from "./Helpers/Constant";
import { InsurancePlan } from "../WholeLife/Helpers/Constant";
import { ChoiceConvenienceData } from "../Common/Appointment/CustomAppointmentData";
import { JourneyTypes } from "../../Helpers/Constants/JourneyTypes";
import { PRODUCT_URLS } from "../../Helpers/Constants/Constant";

export default function WholeLife() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent() }, [location])

  const path = location ? location.pathname.split("/").pop() : null;

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_WHOLE_LIFE_TERM_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_WHOLE_LIFE_HEALTH_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
  }

  // 27514 - Since E*Trade is unable to change the URL on their side, redirect to their landing page when they hit /term-insurance
  useEffect(() => {
    const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    if (location.pathname === '/whole-life' && account.account.accountName.toLowerCase().indexOf("etrade") !== -1) {
      navigate('/landing/etrade-term');
    }
  }, []);

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_WHOLE_LIFE_TERM_KEY);

  const prefill = location.search && location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();

  const newFormState = {
    currentStep: 1,
    pages: {
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  const [formState, setFormState] = useState(initialFormState);

  // When coming from the landing page form, skip to step 2
  useEffect(() => {
    if (location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
  }, []);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        navigate('/whole-life');
      }
    }

    if (!nextStep) {
      return;
    }
    const newFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFormState);
  }, [path]);

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_WHOLE_LIFE_TERM_KEY, JSON.stringify(newFormState));

    navigate(`/whole-life/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  formState.pages.quoter = {
    termLength: 0,
    coverageAmount: 250000,
    healthClass: "PP",
    product: "Whole-Life",
  };

  const steps = [
    {
      step: 1,
      path: 'select-appointment',
      progressTitle: 'Schedule your application',
      title: "WHOLE LIFE  /  SCHEDULE CALL",
      component: (
        <Steps.SelectAppointment
          productUrl={PRODUCT_URLS.WL}
          formState={formState.pages.selectAppointment}
          quoter={formState.pages.quoter}
          journeyType={JourneyTypes.QUOTE_REQUEST}
          data={EasyStepsSchedule}
          sessionKey={SESSION_STORAGE_WHOLE_LIFE_HEALTH_KEY}
          title="Easy Next Steps"
          choiceConvenienceTitle="Choice and Convenience"
          choiceConvenienceData={ChoiceConvenienceData}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('selectAppointment', field, value)}
          onNext={onNext}
          setMultiField={(firstName, lastName, email, configurableQuestions) => {
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                selectAppointment: { ...formState.pages.selectAppointment, firstName: firstName, lastName: lastName, email: email, configurableQuestions: configurableQuestions }
              }
            });
            sessionStorage.setItem(SESSION_STORAGE_WHOLE_LIFE_TERM_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 2,
      path: 'thank-you',
      progressTitle: 'Schedule your application',
      title: "WHOLE LIFE / CALL SCHEDULED",
      component: (
        <Steps.ScheduleAppointment
          quoter={formState.pages.quoter}
          appointment={formState.pages.selectAppointment}
          data={EasyStepsConfirmation}
          isWholeLife={true}
          insurancePlan={InsurancePlan}
          title="What happens next"
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
  ];

  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max.apply(Math, categories.map((o) => {
    return o.categoryStep;
  }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);

  return (
    <div>
      <Helmet>
        <title>{`Whole Life - ${currentStepObj?.progressTitle}`}</title>
      </Helmet>

      <HeaderProgressTracker showCustomizeProgressTracker={true} title={currentStepObj.title}
                             hidden={currentStepObj.hidden} customClassNames={"customize-term"}
                             showContainer={currentStepObj.showContainer}
                             hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true}
                             stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3}
                             whiteBg={currentStepObj.whiteBackground} percent={percent}
                             fullWidth={currentStepObj.fullWidth}>
        <div className="wholeLife pb-3">
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}
